import { DateTime } from 'luxon';

import { formatDate } from 'shared';
import {
  BulkUpdateConsumablesProductSubscription,
  ConsumablesOrder,
  ConsumablesProductSubscription,
  DraftConsumablesOrderItem,
  DraftConsumablesProductSubscription,
  VendorFee
} from 'shared';

/** Human-readable lock moment for consumables AI copy (date from delivery minus 8 days + API time string). */
export function formatConsumablesOrderLockCopyPhrase(
  order: Pick<ConsumablesOrder, 'delivery_date' | 'order_lock_time'> | null | undefined
): string | null {
  if (!order?.delivery_date) return null;
  const lockDate = DateTime.fromISO(order.delivery_date).minus({ days: 8 });
  if (!lockDate.isValid) return null;

  const datePart = formatDate({ date: lockDate, format: 'MED_WITH_SHORT_WEEKDAY' });
  if (order.order_lock_time) {
    return `${datePart} at ${order.order_lock_time}`;
  }
  return datePart;
}

export function nextDeliveryDate(today: DateTime = DateTime.now()): DateTime {
  const secondTuesday = upcomingNthWeekday(today, 2, 2); // 2nd Tuesday (2 = Tuesday)
  const fourthTuesday = upcomingNthWeekday(today, 4, 2); // 4th Tuesday

  const daysUntil2 = secondTuesday.diff(today, 'days').days;
  const daysUntil4 = fourthTuesday.diff(today, 'days').days;

  // Choose the closer Tuesday, then delivery is the following Wednesday (+8 days)
  const lockDay = daysUntil2 <= daysUntil4 ? secondTuesday : fourthTuesday;

  // Delivery occurs the following Wednesday (8 days after Tuesday)
  return lockDay.plus({ days: 8 });
}

export function upcomingNthWeekday(today: DateTime = DateTime.now(), n: number, weekday: number): DateTime {
  let year = today.year;
  let month = today.month; // Note: months are 1-based in Luxon

  // Calculate the first occurrence of the target weekday in the month
  let firstOfMonth = DateTime.local(year, month, 1);
  let firstWeekdayOffset = (weekday - firstOfMonth.weekday + 7) % 7;
  let firstWeekday = firstOfMonth.plus({ days: firstWeekdayOffset });
  let candidate = firstWeekday.plus({ days: (n - 1) * 7 });

  // If candidate is not in the future, calculate for next month
  if (candidate <= today) {
    const nextMonth = firstOfMonth.plus({ months: 1 });
    year = nextMonth.year;
    month = nextMonth.month;

    firstOfMonth = DateTime.local(year, month, 1);
    firstWeekdayOffset = (weekday - firstOfMonth.weekday + 7) % 7;
    firstWeekday = firstOfMonth.plus({ days: firstWeekdayOffset });
    candidate = firstWeekday.plus({ days: (n - 1) * 7 });
  }

  return candidate;
}

type DeliveryFeeItem = ConsumablesProductSubscription | DraftConsumablesProductSubscription | DraftConsumablesOrderItem;

export function calculateDeliveryFee(items: DeliveryFeeItem[], deliveryFees?: VendorFee[] | null): number {
  if (!deliveryFees?.length) return 0;

  const vendorIds = items.reduce((acc, item) => {
    const vendorId = item.consumables_product.vendor_id;
    if (!acc.includes(vendorId)) {
      acc.push(vendorId);
    }
    return acc;
  }, [] as number[]);

  return vendorIds.reduce((total, vendorId) => {
    const fee = deliveryFees.find(fee => fee.vendor_id === vendorId);
    return total + (fee?.amount_cents || 0);
  }, 0);
}

export function buildConsumablesOrderItemsUpdate(
  originalItems: DraftConsumablesOrderItem[],
  clonedItems: DraftConsumablesOrderItem[]
): { orderItems: DraftConsumablesOrderItem[]; wouldMutate: boolean } {
  const originalByProductId = new Map(originalItems.map(item => [item.consumables_product.id, item]));
  const cloneByProductId = new Map(clonedItems.map(item => [item.consumables_product.id, item]));

  const orderItems = clonedItems.map(item => {
    const original = originalByProductId.get(item.consumables_product.id);
    if (item.id == null && original?.id != null) {
      return { ...item, id: original.id };
    }
    return item;
  });

  originalItems.forEach(originalItem => {
    if (originalItem.id == null) return;
    if (cloneByProductId.has(originalItem.consumables_product.id)) return;
    orderItems.push({ ...originalItem, quantity: 0 });
  });

  const wouldMutate = orderItems.some(item => {
    const original = originalByProductId.get(item.consumables_product.id);
    if (original?.id != null) {
      return item.quantity !== original.quantity;
    }
    return item.quantity > 0;
  });

  return { orderItems, wouldMutate };
}

export function buildConsumablesProductSubscriptionsUpdate(
  originalSubscriptions: DraftConsumablesProductSubscription[],
  clonedSubscriptions: DraftConsumablesProductSubscription[]
): { subscriptions: BulkUpdateConsumablesProductSubscription[]; wouldMutate: boolean } {
  const originalQtyByProductId = new Map(
    originalSubscriptions.map(subscription => [subscription.consumables_product.id, subscription.order_quantity])
  );
  const cloneByProductId = new Map(
    clonedSubscriptions.map(subscription => [subscription.consumables_product.id, subscription])
  );

  const subscriptions: BulkUpdateConsumablesProductSubscription[] = clonedSubscriptions.map(subscription => ({
    consumables_product_id: subscription.consumables_product.id,
    quantity: subscription.order_quantity
  }));

  originalSubscriptions.forEach(original => {
    if (cloneByProductId.has(original.consumables_product.id)) return;
    subscriptions.push({
      consumables_product_id: original.consumables_product.id,
      quantity: 0
    });
  });

  const wouldMutate = subscriptions.some(subscription => {
    const originalQty = originalQtyByProductId.get(subscription.consumables_product_id);
    if (originalQty == null) {
      return subscription.quantity > 0;
    }
    return subscription.quantity !== originalQty;
  });

  return { subscriptions, wouldMutate };
}
